import React, { useEffect, useId, useState } from 'react';
import { AlertTriangle, ChevronRight, Filter, Lock, X } from 'lucide-react';
import { Checkbox } from '../ui/Checkbox';
import { Spinner } from '../ui/misc';
import { FACETS } from '../../lib/facets';
import { cx } from '../../lib/cx';

const STATUS_TEXT = {
  no_match: 'No matches',
  error: 'Search failed',
  unavailable: 'Live only — not available for your role',
  stopped: 'Not searched (stopped)',
};

const FacetOption = ({ value, label, count, checked, onToggle, trailing }) => {
  const id = useId();
  return (
    <li className="flex items-center gap-2.5 rounded-md px-1.5 py-1 hover:bg-raised/60">
      <Checkbox id={id} checked={checked} onChange={() => onToggle(value)} />
      <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm">
        <span className={cx('truncate', checked ? 'text-fg' : 'text-fg-2')}>{label || value}</span>
        {trailing}
        <span className="ml-auto tabular-nums text-xs text-muted">{count.toLocaleString()}</span>
      </label>
    </li>
  );
};

const FacetGroup = ({ title, children, action }) => {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id} className="border-t border-line pt-4 first:border-t-0 first:pt-0">
      <div className="mb-1.5 flex items-center justify-between px-1.5">
        <h3 id={id} className="text-xs font-semibold uppercase tracking-wider text-faint">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
};

/** Refine box with a short debounce so typing stays smooth on large result sets. */
const RefineInput = ({ value, onChange }) => {
  const [draft, setDraft] = useState(value);
  const id = useId();
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    if (draft === value) return undefined;
    const timer = window.setTimeout(() => onChange(draft), 250);
    return () => window.clearTimeout(timer);
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="px-1.5">
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-faint">
        Refine results
      </label>
      <div className="relative">
        <Filter size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-faint" aria-hidden="true" />
        <input
          id={id}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="field h-9 pl-8 pr-8"
          autoComplete="off"
        />
        {draft && (
          <button
            type="button"
            onClick={() => { setDraft(''); onChange(''); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-faint hover:text-fg"
            aria-label="Clear refine text"
          >
            <X size={13} aria-hidden="true" />
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted">Matches any field in the results.</p>
    </div>
  );
};

/**
 * Post-search filters. The Lab list doubles as per-lab search progress:
 * labs still searching show a spinner, and labs with nothing to show are
 * listed (with the reason) under "No results".
 */
export const FacetRail = ({
  facetCounts, filters, onToggle, onClearFacet, refine, onRefineChange, labSummary, searching,
}) => {
  const [showEmpty, setShowEmpty] = useState(false);

  const pending = Object.entries(labSummary)
    .filter(([, s]) => s.status === 'pending' || (s.status === 'searching' && !s.results))
    .map(([group]) => group)
    .sort();
  const empty = Object.entries(labSummary)
    .filter(([, s]) => ['no_match', 'error', 'unavailable', 'stopped'].includes(s.status))
    .sort(([a], [b]) => a.localeCompare(b));

  const renderFacet = (key) => {
    const entries = facetCounts[key] || [];
    const selected = filters[key] || [];
    if (key !== 'lab' && entries.length <= 1 && !selected.length) return null;
    return (
      <FacetGroup
        key={key}
        title={FACETS[key].label}
        action={selected.length > 0 && (
          <button type="button" onClick={() => onClearFacet(key)} className="text-xs text-accent-fg hover:underline">
            Clear
          </button>
        )}
      >
        <ul>
          {entries.map(({ value, count }) => {
            const status = key === 'lab' ? labSummary[value] : null;
            return (
              <FacetOption
                key={value}
                value={value}
                label={key === 'lab' ? value.replace(/\s*-\s*Custom Databases$/, ' (custom)') : value}
                count={count}
                checked={selected.includes(value)}
                onToggle={(v) => onToggle(key, v)}
                trailing={status && (
                  <>
                    {status.status === 'searching' && <Spinner size={12} label="Still searching" />}
                    {status.truncated && (
                      <span className="text-xs text-warning" title="More matches than shown; refine your search">
                        <AlertTriangle size={12} aria-label="Some results not shown" />
                      </span>
                    )}
                  </>
                )}
              />
            );
          })}
        </ul>

        {key === 'lab' && pending.length > 0 && (
          <div className="mt-2 flex items-start gap-2 px-1.5 text-xs text-muted" aria-live="off">
            <Spinner size={12} className="mt-0.5" />
            <span>Searching {pending.length === 1 ? pending[0] : `${pending.length} more labs`}…</span>
          </div>
        )}

        {key === 'lab' && empty.length > 0 && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowEmpty((v) => !v)}
              aria-expanded={showEmpty}
              className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-xs text-muted hover:text-fg"
            >
              <ChevronRight size={13} className={cx('transition-transform', showEmpty && 'rotate-90')} aria-hidden="true" />
              No results from {empty.length} lab{empty.length === 1 ? '' : 's'}
              {empty.some(([, s]) => s.status === 'error') && (
                <AlertTriangle size={12} className="text-danger" aria-label="includes failures" />
              )}
            </button>
            {showEmpty && (
              <ul className="mt-1 space-y-1.5 px-1.5 pb-1">
                {empty.map(([group, s]) => (
                  <li key={group} className="text-xs">
                    <span className="flex items-center gap-1.5 text-fg-2">
                      {s.status === 'error' && <AlertTriangle size={12} className="text-danger" aria-hidden="true" />}
                      {s.status === 'unavailable' && <Lock size={12} className="text-faint" aria-hidden="true" />}
                      {group.replace(/\s*-\s*Custom Databases$/, ' (custom)')}
                    </span>
                    <span className="block pl-[18px] text-muted">
                      {STATUS_TEXT[s.status]}
                      {s.status === 'error' && s.messages[0] ? `: ${s.messages[0].replace(/^Unexpected error processing [^:]+:\s*/, '')}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </FacetGroup>
    );
  };

  return (
    <div className="space-y-4 pb-6">
      <RefineInput value={refine} onChange={onRefineChange} />
      <div className="space-y-4">
        {['lab', 'region', 'specimen', 'referral'].map(renderFacet)}
      </div>
      {searching && <span className="sr-only">Results are still arriving.</span>}
    </div>
  );
};
